import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../../lib/scroll";

const LINKS = [
  { label: "About", id: "#about" },
  { label: "Why I'm Running", id: "#why" },
  { label: "Priorities", id: "#priorities" },
  { label: "Endorsements", id: "#endorsements" },
  { label: "Vote", id: "#vote" },
  { label: "Donate", id: "#donate" },
  { label: "Contact", id: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-slate-200/70" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          onClick={() => go("#top")}
          className="font-display font-extrabold tracking-tight text-lg text-slate-900 flex items-center gap-2"
          data-testid="nav-wordmark"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-teal-600" />
          NOMAN&nbsp;ZAHEER
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-600 hover:text-teal-700 transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#join")}
            className="rounded-full bg-slate-900 text-white text-sm font-semibold px-5 py-2.5 hover:bg-teal-700 transition-colors"
            data-testid="nav-join-button"
          >
            Join The Campaign
          </button>
        </div>

        <button
          className="lg:hidden text-slate-900 p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
            data-testid="nav-mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left py-3 text-base font-medium text-slate-700 border-b border-slate-100"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#join")}
                className="mt-3 rounded-full bg-slate-900 text-white font-semibold px-5 py-3"
              >
                Join The Campaign
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
